import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren, useEffect, useState, type ChangeEvent, useMemo } from 'react';

import {
  Box,
  Container,
  IntegrationCard,
  Link,
  Pagination,
  Heading,
  Text,
  Icon,
  Combobox,
  AccessibleDescription,
  PreFooterCta,
  LoadingIndicator,
  SearchBar,
  LucidHead,
} from '@lucid/components';
import partition from 'lodash/partition';
import { graphql, type HeadFC, navigate } from 'gatsby';
import { useUrlPathUpdate, useSmartlingStrings, useURLParams } from '@lucid/hooks';
import { isBrowser } from '@lucid/utils';
import kebabCase from 'lodash/kebabCase';
import { GatsbyImage } from 'gatsby-plugin-image';
import debounce from 'lodash/debounce';

import type { IntegrationCardProps, Lucid } from 'types/types';

interface MarketplaceProps {
  data: {
    allMarketplaceIntegration: {
      nodes: Lucid.Marketplace.Integration[];
    };
    category: {
      nodes: Lucid.Marketplace.CategoryNode[];
    };
  };
  pageContext: Lucid.Context.Marketplace.Data & Lucid.Context.Keys & Lucid.Context.Pagination;
  location: Location;
}

interface ComboboxOptionType {
  /** The text to be shown in the Dropdown menu. */
  text: string;
  /** The unique value for the text in the Dropdown menu. */
  value: string;
}

const Marketplace: FC<PropsWithChildren<MarketplaceProps>> = ({ data, pageContext }) => {
  const {
    shortCode,
    urlLangPrefix,
    localeId,
    pageType,
    nid,
    shouldHideSsr,
    page: ssrPage,
    pageSize,
    categoryLabel,
    slug,
    categoryList,
    metatags,
    product,
  } = pageContext;

  const {
    allMarketplaceIntegration: { nodes: allMarketplaceIntegrations },
    category: {
      nodes: [category],
    },
  } = data;

  const filterIntegrations = (integrations: Lucid.Marketplace.Integration[], search: string) => {
    if (!search) {
      return integrations;
    }

    return integrations.filter(integration => integration.title.toLowerCase().includes(search.toLowerCase()));
  };

  const integrationsWithNewTag = useMemo(
    () =>
      allMarketplaceIntegrations
        .reduce<
          (Lucid.Marketplace.Integration & Pick<IntegrationCardProps, 'isNew'>)[]
        >((prev, curr) => [...prev, { ...curr, isNew: curr.categories.includes('New') }], [])
        .filter(integration => !integration.categories.includes('Hidden')),
    [allMarketplaceIntegrations],
  );

  // Display 'new' integrations first
  const allSortedIntegrations = useMemo(
    () => partition(integrationsWithNewTag, ({ isNew }) => isNew).flat(),
    [integrationsWithNewTag],
  );

  const categoryIntegrations = useMemo(
    () =>
      categoryLabel === 'All integrations'
        ? allSortedIntegrations
        : allSortedIntegrations.filter(integration => integration.categories.includes(categoryLabel)),
    [categoryLabel, allSortedIntegrations],
  );

  const categorySectionMapping = [
    { label: 'Getting started', categoryLabels: ['Lucidspark', 'Lucidchart', 'Lucidscale', 'Featured', 'New'] },
    { label: 'Categories', categoryLabels: [] }, // empty array is a catch-all for any unsorted categories
    {
      label: 'Teams',
      categoryLabels: [
        'IT',
        'Engineering',
        'Project management',
        'Sales and marketing',
        'Product management',
        'Education',
        'HR',
      ],
    },
  ];

  const categorySections = categorySectionMapping.map(({ label, categoryLabels }) => ({
    label,
    categories: (categoryList || [])
      .filter(item =>
        categoryLabels.length === 0
          ? !categorySectionMapping.some(section => section.categoryLabels.includes(item.label))
          : categoryLabels.includes(item.label),
      )
      .sort((a, b) => categoryLabels.indexOf(a.label) - categoryLabels.indexOf(b.label)),
  }));

  const { paramValue, updateURLParam } = useURLParams('s');
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_search_no_results'],
    shortCode,
  });
  const [currentIntegrations, setCurrentIntegrations] =
    useState<(Lucid.Marketplace.Integration & Pick<IntegrationCardProps, 'isNew'>)[]>(categoryIntegrations);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(paramValue);
  const [page, setPage] = useState<null | number>(ssrPage);
  const searchBarRef = React.createRef<HTMLInputElement>();

  useUrlPathUpdate(
    `/marketplace/${categoryLabel !== 'All integrations' ? `${slug}/` : ''}${page && page > 1 ? `${page}` : ''}`.replace(
      /\/$/,
      '',
    ),
  );

  const updateSearchResults = useMemo(
    () =>
      debounce((value: string) => {
        updateURLParam(value);
        setSearchQuery(value);
        setPage(1);
      }, 800),
    [updateURLParam],
  );

  useEffect(() => {
    // set searchQuery value from 's' URL param on page load
    if (searchBarRef.current?.value === paramValue) {
      setSearchQuery(paramValue);
    }
  }, [paramValue, searchBarRef]);

  useEffect(() => {
    // update page when URL pathname changes
    if (isBrowser() && !window.location.pathname.endsWith(`/${page}`) && page !== 1) {
      setPage(ssrPage);
    }
  }, [page, ssrPage, searchBarRef]);

  useEffect(() => {
    // update search bar value when 's' url param changes
    // this is necessary for on-page navigation which updates the URL but doesn't trigger a page reload
    if (isBrowser() && searchBarRef.current && !isLoading && searchBarRef.current !== document.activeElement) {
      searchBarRef.current.value = paramValue;
    }
  }, [isLoading, paramValue, searchBarRef]);

  useEffect(
    () => setCurrentIntegrations(filterIntegrations(categoryIntegrations, searchQuery)),
    [categoryIntegrations, categoryLabel, paramValue, searchQuery],
  );

  useEffect(() => {
    if (paramValue === searchQuery) {
      setIsLoading(false);
    }
  }, [currentIntegrations, paramValue, searchQuery]);

  const preFooterCta = [
    {
      final_url: 'https://lucid.co/developers',
      default_link_text: 'Developer portal',
    },
  ] as Lucid.Directus.Generated.Links[];

  const handleSearch = (value: string) => {
    setIsLoading(true);
    updateSearchResults(value);
  };

  const handleCategoryChange = (filterOption: ComboboxOptionType) => {
    if (filterOption.value) {
      navigate(filterOption.value);
    }
  };

  const handleNewPage = (value: number) => {
    setPage(value);

    if (isBrowser()) {
      searchBarRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  };

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={[]}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      {category && category.banner && page === 1 && (
        <Box
          css={theme => ({
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            height: '396px',
            maxWidth: '1020px',
            margin: `${theme.space.s64}px auto 0 auto`,
          })}
        >
          <Box
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: theme.colors.coolGrey[100],
              color: 'white',
              padding: theme.space.s48,
            })}
          >
            <Heading
              as="h1"
              css={theme => ({ color: theme.colors.white, marginBottom: theme.space.s16 })}
              variant="heading2XL"
            >
              Lucid integrations marketplace
            </Heading>
            <Text css={theme => ({ color: theme.colors.white })} variant="paragraphM300">
              Connect Lucid to your favorite industry-leading apps
            </Text>
          </Box>
          <GatsbyImage
            alt="Lucid integrations marketplace - Connect Lucid to your favorite industry-leading apps"
            css={{
              aspectRatio: '510/396',
              display: 'block',
              objectFit: 'cover',
            }}
            image={category.banner.childImageSharp.gatsbyImageData}
            loading="lazy"
            objectFit="cover"
          />
        </Box>
      )}
      <Container
        css={theme => ({
          display: 'grid',
          gridTemplateColumns: '1fr',
          columnGap: theme.space.s64,
          paddingBottom: theme.space.s80,
          marginTop: theme.space.s64,
          [theme.breakpoints.bp900.min]: {
            gridTemplateColumns: '184px 1fr',
          },
        })}
      >
        <Box css={theme => ({ [theme.breakpoints.bp900.min]: { display: 'none' } })}>
          <Combobox
            css={theme => ({
              marginBottom: theme.space.s32,
            })}
            id="marketplace-category-filter"
            onChange={handleCategoryChange}
          >
            <Combobox.Box
              css={theme => ({
                border: 'none',
                backgroundColor: theme.colors.coolGrey[0],
                padding: `11px ${theme.space.s16}px`,
              })}
            >
              <Combobox.Input hasCarot startValue={categoryLabel} />
            </Combobox.Box>
            <Combobox.List variant="simple">
              <Combobox.Option text="All integrations" value="/marketplace">
                <Link css={{ display: 'block' }} href="/marketplace" onClick={e => e.preventDefault()}>
                  <Combobox.Option.Text />
                </Link>
              </Combobox.Option>
              {categorySections.map(({ categories }) =>
                categories.map(({ label, path }) => (
                  <Combobox.Option key={`${label}-${path}`} text={label} value={path}>
                    <Link css={{ display: 'block' }} href={path}>
                      <Combobox.Option.Text />
                    </Link>
                  </Combobox.Option>
                )),
              )}
            </Combobox.List>
          </Combobox>
        </Box>
        <Box
          aria-label="marketplace navigation"
          as="nav"
          css={theme => ({ textAlign: 'right', display: 'none', [theme.breakpoints.bp900.min]: { display: 'block' } })}
        >
          <Link
            css={theme => ({
              background: theme.colors.blue[10],
              padding: ` 10px ${theme.space.s16}px`,
              marginBottom: theme.space.s16,
              borderRadius: theme.radii.r8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            })}
            href="/marketplace"
            variant="simpleUnderline"
          >
            All integrations
            <Icon css={theme => ({ fontSize: theme.fontSizes.fs12 })} type="CarotRight" />
          </Link>
          {categorySections.map(({ label, categories }) => (
            <Box key={label}>
              <Heading as="h2" id={`section-${kebabCase(label)}`} variant="headingM">
                {label}
              </Heading>
              <ul aria-labelledby={`section-${kebabCase(label)}`} css={{ listStyle: 'none', margin: 0 }}>
                {categories.map(({ label, path }) => (
                  <li key={`${label}-${path}`} css={theme => ({ paddingBottom: theme.space.s16 })}>
                    <Link
                      css={theme => [label === categoryLabel && { fontWeight: theme.fontWeights.fw600 }]}
                      href={path}
                      variant="simpleUnderline"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Box>
          ))}
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <SearchBar
            ref={searchBarRef}
            aria-label="Search Lucid integrations"
            defaultValue={searchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleSearch(e.target.value);
            }}
            placeholder={
              categoryLabel === 'All integrations'
                ? 'Discover new ways to work...'
                : `Search ${
                    categoryLabel.includes('Lucid') ? categoryLabel : categoryLabel.toLowerCase()
                  } integrations...`
            }
          />
          <AccessibleDescription
            isForScreenReaderOnly
            aria-live="polite"
            SRText={`${currentIntegrations.length} search result${currentIntegrations.length === 1 ? '' : 's'}`}
          />
          <Box
            css={theme => ({
              marginTop: theme.space.s64,
              marginBottom: theme.space.s48,
            })}
          >
            <Box
              css={theme => ({
                display: 'flex',
                gap: theme.space.s24,
                alignItems: 'baseline',
                justifyContent: 'space-between',
              })}
            >
              <Heading
                as={!category?.banner || page !== 1 ? 'h1' : 'h2'}
                css={theme => ({ marginBottom: theme.space.s24 })}
                variant="headingXL"
              >
                {categoryLabel}
              </Heading>
              {page && page > 1 && <Text variant="paragraphS400">{`page ${page}`}</Text>}
            </Box>
            {category && category.description && <Text variant="paragraphM400">{category.description}</Text>}
          </Box>
          <Box css={{ position: 'relative' }}>
            {(!page || isLoading) && (
              <Box
                aria-busy={isLoading}
                aria-label="Loading"
                aria-live="polite"
                css={theme => ({
                  zIndex: theme.zIndices.z10,
                  backgroundColor: theme.colors.white,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: `-${theme.space.s16}px`,
                  bottom: 0,
                })}
              >
                <LoadingIndicator />
              </Box>
            )}
            {page && (
              <Pagination<IntegrationCardProps>
                aria-label="marketplace search results"
                items={currentIntegrations}
                itemsPerPage={pageSize}
                onClick={handleNewPage}
                pageLink={page =>
                  `/marketplace/${categoryLabel !== 'All integrations' ? `${slug}/` : ''}${page > 1 ? `${page}` : ''}${`?${new URLSearchParams({ s: searchQuery }).toString()}`}`
                }
                startPage={page}
                visiblePageCount={3}
              >
                {({ visibleItems }) =>
                  visibleItems.length > 0 ? (
                    <Box
                      css={theme => ({
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gridGap: theme.space.s48,
                        alignItems: 'stretch',
                        justifyItems: 'stretch',
                        [theme.breakpoints.bp900.min]: {
                          gridTemplateColumns: '1fr 1fr',
                        },
                        [theme.breakpoints.bp1024.min]: {
                          gridTemplateColumns: '1fr 1fr 1fr',
                        },
                      })}
                    >
                      {visibleItems.map((vItem, index) => (
                        <IntegrationCard
                          key={`${vItem.title} - ${index + 1}`}
                          icon48={vItem.icon48}
                          isNew={vItem.isNew}
                          link={{
                            id: `${vItem.packageId?.split('-')[0]}-${kebabCase(vItem.title)}`,
                            final_url: `/marketplace/${vItem.packageId?.split('-')[0]}/${kebabCase(vItem.title)}`,
                          }}
                          shortDescription={vItem.shortDescription}
                          title={vItem.title}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Heading as="h2" variant="headingM">
                      {withSmartling('mktg_search_no_results')}
                    </Heading>
                  )
                }
              </Pagination>
            )}
          </Box>
        </Box>
      </Container>
      <PreFooterCta
        backgroundColor="blue.10"
        ctas={preFooterCta}
        displayAsButton={false}
        heading="Don't see what you're looking for? Build custom integrations with our developer platform."
      />
    </Layout>
  );
};

export default Marketplace;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query MarketplaceQuery($categoryLabel: String!) {
    allMarketplaceIntegration(sort: { title: ASC }) {
      nodes {
        title
        shortDescription
        packageId
        categories
        icon48 {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, quality: 100)
          }
        }
      }
    }
    category: allMarketplaceCategoriesJson(filter: { label: { eq: $categoryLabel } }, limit: 1) {
      nodes {
        banner {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, width: 2040, height: 1584, quality: 100)
          }
        }
        description
        label
      }
    }
  }
`;
